import {
  Box,
  InputCheckBox as CheckBox,
  Icon,
  Text,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'
import isNil from 'lodash/isNil'

import { observer } from 'mobx-react'

import createComputed from 'modules/core/hooks/createComputed'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import {
  CHANNEL_ORDER,
  CHANNEL_WIDTH,
} from 'modules/notifications/components/Preferences/PreferencesList/_Category/constants'
import type { CategoryPreferences } from 'modules/notifications/components/Preferences/PreferencesList/hooks/useComputedPreferences'
import type UserPreference from 'modules/notifications/store/models/UserPreference'

const getChannelsLabel = (type: string) => {
  switch (type) {
    case 'email':
      return i18n.t('notifications.preferences.channels.email')
    case 'push':
      return i18n.t('notifications.preferences.channels.push')
    default:
      throw new Error(`Unknown translation for type: ${type}`)
  }
}

/**
 * Computes the state of all the channel notifiers.
 * - True/False: Whether all notifiers are enabled/disabled
 * - 'mixed': If some notifiers are enabled and some are disabled
 * - undefined: If there are no notifiers
 */
const useChannelEnabled = createComputed((channel: string, category: CategoryPreferences) => {
  let currentValue: boolean | undefined

  for (const notifierPreferences of Object.values(category.notifiers)) {
    const channelData = notifierPreferences.channelData[channel]
    const preference = notifierPreferences.channelPreferences[channel]

    if (isNil(preference) || !channelData?.editable) {
      continue
    }

    if (currentValue === undefined) {
      currentValue = preference.isEnabled
    } else if (currentValue !== preference.isEnabled) {
      return 'mixed'
    }
  }

  return currentValue
})

export interface ChannelCheckProps {
  channel: string
  category: CategoryPreferences
  onPreferenceUpdated: (preference: UserPreference, newValue: boolean) => Promise<void>
}

export const ChannelCheck = observer(
  ({ channel, category, onPreferenceUpdated }: ChannelCheckProps) => {
    const channelEnabled = useChannelEnabled(channel, category).get()
    const [tooltipOpen, toggleTooltip] = useToggle()

    const { performAction } = useAsyncAction(async () => {
      const newValue = !channelEnabled

      await Promise.all(
        Object.values(category.notifiers)
          .filter((notifierPreferences) => notifierPreferences.channelData[channel]?.editable)
          .map((notifierPreferences) => notifierPreferences.channelPreferences[channel])
          .filter((preference) => preference && preference?.isEnabled !== newValue)
          .map((preference) => onPreferenceUpdated(preference!, newValue))
      )

      track(trackingEvents.inbox.preferences.updateCategory, {
        changed_to: newValue,
        category: category.identifier,
        channel,
      })
    })

    return (
      <Box
        key={channel}
        width={CHANNEL_WIDTH}
        flexDirection="column"
        alignItems="center"
        textAlign="center"
        justifyContent="flexEnd"
        gap="s8"
      >
        <Text size="100" weight="medium">
          {getChannelsLabel(channel ?? '')}
        </Text>
        {isNil(channelEnabled) ? (
          <Tooltip.Alt
            open={tooltipOpen}
            toggle={toggleTooltip}
            attachment="bottom center"
            label={i18n.t('notifications.preferences.channel_not_configurable')}
          >
            <CheckBox checked={false} disabled />
          </Tooltip.Alt>
        ) : (
          <CheckBox
            checked={!!channelEnabled}
            indeterminate={channelEnabled === 'mixed'}
            onClick={performAction}
          />
        )}
      </Box>
    )
  }
)

export interface Props {
  category: CategoryPreferences
  onPreferenceUpdated: (preference: UserPreference, newValue: boolean) => Promise<void>
}

export const CategoryHeader = observer(({ category, onPreferenceUpdated }: Props) => {
  return (
    <Box flexDirection="row" paddingRight="s24" gap="s24">
      <Box
        flexDirection="row"
        gap="s12"
        flexGrow
        aria-label="notification-category"
        alignItems="center"
      >
        <Icon.Large icon={category.icon} />
        <Text color="grey1000" size="400" weight="medium">
          {category.title}
        </Text>
      </Box>
      {CHANNEL_ORDER.map((channel) => (
        <ChannelCheck
          key={channel}
          channel={channel}
          category={category}
          onPreferenceUpdated={onPreferenceUpdated}
        />
      ))}
    </Box>
  )
})
